import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from "@mui/material";
import { useEffect, useState } from "react";
import SearchableSelect from "../common/SearchableSelect";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const inputClass = `
  w-full h-14
  rounded-md
  border border-surface-200
  bg-white
  px-3.5
  text-[16px] text-surface-800
  outline-none
  transition-colors
  focus:border-primary-500
  focus:ring-1 focus:ring-primary-500
`;

const labelClass = `
  absolute
  left-3
  top-0
  -translate-y-1/2
  bg-white
  px-1.5
  text-[13px]
  text-surface-500
  pointer-events-none
  z-10
`;

const formatCleanNumber = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const num = parseFloat(val);
    return Number.isNaN(num) ? '' : String(num);
}

function Field({ label, error, children }) {
  return (
    <div className="relative">
      <label className={labelClass}>{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

export default function AddMonthlyAllocationDialog({
  open,
  onClose,
  onSave,
  loading = false,
  mode = "add",
  allocation = null,
  categories = [],
}) {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const [form, setForm] = useState({
    allocationId: null,
    year: currentYear,
    month: currentMonth,
    categoryCode: "",
    allocationDays: "",
    allocationHours: "",
  });

  const [errors, setErrors] = useState({});

  const isView = mode === "view";
  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open) return;
    setErrors({});

    if (allocation && (mode === "edit" || mode === "view")) {
      setForm({
        allocationId: allocation.HMA_ALLOCATION_ID || allocation.id,
        year: Number(allocation.HMA_YEAR || allocation.year || currentYear),
        month: Number(allocation.HMA_MONTH || allocation.month || currentMonth),
        categoryCode: allocation.HMA_CATEGORY_CODE || allocation.categoryCode || "",
        allocationDays: formatCleanNumber(allocation.HMA_ALLOCATION_DAYS ?? allocation.allocationDays ?? ""),
        allocationHours: formatCleanNumber(allocation.HMA_ALLOCATION_HOURS ?? allocation.allocationHours ?? ""),
      });
    } else {
      setForm({
        allocationId: null,
        year: currentYear,
        month: currentMonth,
        categoryCode: categories[0]?.HEC_CATEGORY_CODE || "",
        allocationDays: "",
        allocationHours: "",
      });
    }
  }, [open, mode, allocation, categories, currentYear, currentMonth]);

  const handleChange = (field) => (e) => {
    const val = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: val };

      // Auto-suggest allocation hours as allocationDays * 8 if hours was untouched/empty
      if (field === "allocationDays") {
        const numDays = parseFloat(val);
        if (!isNaN(numDays) && numDays >= 0) {
          next.allocationHours = String(numDays * 8);
        } else {
          next.allocationHours = "";
        }
      }
      return next;
    });

    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSave = () => {
    if (isView) return;

    const validationErrors = {};
    if (!form.year || isNaN(form.year)) validationErrors.year = "Year is required.";
    if (!form.month) validationErrors.month = "Month is required.";
    if (!form.categoryCode) validationErrors.categoryCode = "Employee category is required.";
    if (form.allocationDays === "" || isNaN(form.allocationDays) || Number(form.allocationDays) < 0) {
      validationErrors.allocationDays = "Valid allocation days is required.";
    }
    if (form.allocationHours === "" || isNaN(form.allocationHours) || Number(form.allocationHours) < 0) {
      validationErrors.allocationHours = "Valid allocation hours is required.";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    onSave({
      allocationId: form.allocationId,
      year: Number(form.year),
      month: Number(form.month),
      categoryCode: form.categoryCode,
      allocationDays: Number(form.allocationDays),
      allocationHours: Number(form.allocationHours),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="!px-6 !pt-5 !pb-3">
        <span className="text-[21px] font-medium text-surface-900">
          {mode === "view"
            ? "View Monthly Allocation"
            : mode === "edit"
            ? "Edit Monthly Allocation"
            : "New Monthly Allocation"}
        </span>
      </DialogTitle>

      <DialogContent className="!px-6 !py-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Year */}
          <Field label="Year *" error={errors.year}>
            <input
              type="number"
              value={form.year}
              onChange={handleChange("year")}
              disabled={isView}
              className={`${inputClass} ${
                isView ? "bg-gray-50 text-gray-500 cursor-not-allowed" : ""
              } ${errors.year ? "border-red-400" : ""}`}
            />
          </Field>

          {/* Month */}
          <Field label="Month *" error={errors.month}>
            <SearchableSelect
              value={form.month}
              // handleChange reads e.target.value, so hand it the same shape.
              onChange={(v) => handleChange("month")({ target: { value: v } })}
              options={MONTHS}
              disabled={isView}
              searchPlaceholder="Search month..."
              className={`${inputClass} cursor-pointer ${
                isView ? "bg-gray-50 text-gray-500 cursor-not-allowed" : ""
              } ${errors.month ? "border-red-400" : ""}`}
            />
          </Field>

          {/* Employee Category */}
          <div className="sm:col-span-2">
            <Field label="Employee Category *" error={errors.categoryCode}>
              <select
                value={form.categoryCode}
                onChange={handleChange("categoryCode")}
                disabled={isView}
                className={`${inputClass} cursor-pointer ${
                  isView ? "bg-gray-50 text-gray-500 cursor-not-allowed" : ""
                } ${errors.categoryCode ? "border-red-400" : ""}`}
              >
                <option value="">-- Select Category --</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.code}>
                    {cat.name} ({cat.code})
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* Allocation Days */}
          <Field label="Allocation Days *" error={errors.allocationDays}>
            <input
              type="number"
              step="0.5"
              placeholder="e.g. 26"
              value={form.allocationDays}
              onChange={handleChange("allocationDays")}
              disabled={isView}
              className={`${inputClass} ${
                isView ? "bg-gray-50 text-gray-500 cursor-not-allowed" : ""
              } ${errors.allocationDays ? "border-red-400" : ""}`}
            />
          </Field>

          {/* Allocation Hours */}
          <Field label="Allocation Hours *" error={errors.allocationHours}>
            <input
              type="number"
              step="0.5"
              placeholder="e.g. 208"
              value={form.allocationHours}
              onChange={handleChange("allocationHours")}
              disabled={isView}
              className={`${inputClass} ${
                isView ? "bg-gray-50 text-gray-500 cursor-not-allowed" : ""
              } ${errors.allocationHours ? "border-red-400" : ""}`}
            />
          </Field>
        </div>
      </DialogContent>

      <DialogActions className="!px-6 !py-3">
        <Button onClick={onClose} className="!text-primary-600">
          {isView ? "CLOSE" : "CANCEL"}
        </Button>
        {!isView && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            className="!bg-primary-600 hover:!bg-primary-700"
          >
            {loading ? "SAVING..." : isEdit ? "UPDATE" : "SAVE"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}